import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type InvalidGroup,
  type NewItemDefaults,
  type UiGroup,
  addChildGroupTo,
  allErrors,
  applyInheritedDefaults,
  fromModel,
  mapGroup,
  mkGroup,
  mkItem,
  removeGroupNode,
} from './options-builder.utils';
import type { VariantComboGroupView } from '../../../../../services/variants-combos/variants-combos.service';

type Params = {
  opened: boolean;
  isLoading: boolean;
  groups: VariantComboGroupView[];
  itemDefaults?: NewItemDefaults;
};

// Architectural summary: Owns the Options Builder drawer's draft state machine (the editable UI tree, which
export function useOptionsBuilderDraft({ opened, isLoading, groups, itemDefaults }: Params) {
  const [draft, setDraft] = useState<UiGroup[]>([]);
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  const [showErrors, setShowErrors] = useState(false);
  const initedRef = useRef(false);
  const itemDefaultsRef = useRef<NewItemDefaults>(itemDefaults ?? {});
  itemDefaultsRef.current = itemDefaults ?? {};

  useEffect(() => {
    if (!opened) {
      initedRef.current = false;
      return;
    }
    if (!isLoading && !initedRef.current) {
      setDraft(applyInheritedDefaults(fromModel(groups), itemDefaultsRef.current));
      setEditing(new Set());
      setShowErrors(false);
      initedRef.current = true;
    }
  }, [opened, isLoading, groups]);

  const errors: InvalidGroup[] = useMemo(() => allErrors(draft), [draft]);

  const toggleEdit = useCallback((id: string, force?: boolean) => {
    setEditing((prev) => {
      const next = new Set(prev);
      const want = force === undefined ? !next.has(id) : force;
      if (want) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const addItem = useCallback((groupId: string) => {
    setDraft((gs) => mapGroup(gs, groupId, (g) => ({ items: [...g.items, mkItem('', itemDefaultsRef.current)] })));
  }, []);

  const addChild = useCallback((itemId: string) => {
    const child = mkGroup({ type: 'variant', items: [mkItem('', itemDefaultsRef.current)] });
    setDraft((gs) => addChildGroupTo(gs, itemId, child));
    setEditing((prev) => new Set(prev).add(child.id));
  }, []);

  const delGroup = useCallback((g: UiGroup) => {
    if (g.items.length > 0 && !window.confirm(`למחוק את הקבוצה "${g.name || 'ללא שם'}"?`)) return;
    setDraft((gs) => removeGroupNode(gs, g.id));
  }, []);

  const addGroup = useCallback(() => {
    const g = mkGroup({ type: 'variant', items: [mkItem('', itemDefaultsRef.current), mkItem('', itemDefaultsRef.current)] });
    setDraft((gs) => [...gs, g]);
    setEditing((prev) => new Set(prev).add(g.id));
  }, []);

  const revealErrors = useCallback((): boolean => {
    if (errors.length === 0) return false;
    setShowErrors(true);
    setEditing((prev) => {
      const next = new Set(prev);
      errors.forEach((e) => next.add(e.id));
      return next;
    });
    return true;
  }, [errors]);

  return { draft, setDraft, editing, toggleEdit, showErrors, errors, addItem, addChild, delGroup, addGroup, revealErrors };
}
